import React, { useState, useEffect } from 'react';
import { Modal, ModalActions, ModalBody } from 'components/ui/modal';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import { useGroups } from 'hooks/unifiedview/useReports';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import LoadingCircle from 'components/ui/LoadingCircle';
import Alert from 'components/ui/Alert';
import classNames from 'classnames';
import TextArea from 'components/v2/forms/TextArea';
import ToggleSwitchGroup from 'components/v2/ToggleSwitchGroup';
import { partition } from 'lodash';
import { useTranslation } from 'react-i18next';
const defaultReport = {
    name: '',
    description: '',
    group: null,
    region: null,
    shared: true,
};
export default function SaveModal({ report, isOpen, onClose, onSave, isNew }) {
    const { data: groupsData, isSuccess, isLoading } = useGroups();
    const [errors, setErrors] = useState({});
    const [formFields, setFormFields] = useState(defaultReport);
    const [groups, regions] = partition(groupsData, ['parent', null]);
    const { t } = useTranslation();
    useEffect(() => {
        let group = {};
        const region = report
            ? regions.find((data) => data._id === report.groupId) || {}
            : {};
        if (region === null || region === void 0 ? void 0 : region.parent) {
            group = groups.find((data) => data._id === region.parent);
        }
        else if (report === null || report === void 0 ? void 0 : report.groupId) {
            group = groups.find((data) => data._id === report.groupId);
        }
        setFormFields(Object.assign({ group,
            region }, (report || defaultReport)));
    }, [report]);
    const activeRegions = regions.filter((region) => { var _a; return region.parent === ((_a = formFields === null || formFields === void 0 ? void 0 : formFields.group) === null || _a === void 0 ? void 0 : _a._id); }) || [];
    function handleSave() {
        const errors = validate();
        setErrors(errors);
        if (Object.keys(errors).length === 0 && onSave) {
            const { name, group, region, description, shared } = formFields;
            const data = Object.assign({ name, groupId: (region === null || region === void 0 ? void 0 : region._id) || (group === null || group === void 0 ? void 0 : group._id), description,
                shared }, (report && !isNew && { id: report._id }));
            onSave(data);
        }
    }
    const actionLabel = isNew ? t('SAVE_NEW') : t('SAVE');
    const title = isNew ? t('SAVE_NEW_REPORT') : t('UPDATE_REPORT');
    function handleChange(field) {
        return function (value) {
            setFormFields((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        };
    }
    const primaryAction = {
        label: actionLabel,
        onClick: handleSave,
    };
    const secondaryActions = [
        {
            label: t('CANCEL'),
            onClick: onClose,
        },
    ];
    function handleSelectGroup(group) {
        setFormFields((prev) => (Object.assign(Object.assign({}, prev), { group, region: null })));
    }
    function handleSelectRegion(region) {
        setFormFields((prev) => (Object.assign(Object.assign({}, prev), { region })));
    }
    function renderErrors() {
        const errorList = Object.values(errors);
        if (errorList.length === 0) {
            return;
        }
        return ((React.createElement(Alert, { dataTestId: "Alert-2ef35e64", type: "error", title: t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES'), className: "mb-4" },
            React.createElement("ul", { "data-testid": "ul-19a8cec1" }, errorList.map((message, i) => (React.createElement("li", { "data-testid": `li-e4aad8af-${i}`, key: i }, message)))))));
    }
    function validate() {
        const errors = {};
        const maxNameLength = 100;
        const { name, group } = formFields;
        if (!(name === null || name === void 0 ? void 0 : name.trim())) {
            errors.name = t('PLEASE_PROVIDE_THE_REPORT_NAME');
        }
        if ((name === null || name === void 0 ? void 0 : name.length) > maxNameLength) {
            errors.name = t('TOO_BIG_AMOUNT_ERROR', { maxAmount: maxNameLength });
        }
        if (!group) {
            errors.group = t('PLEASE_SELECT_A_GROUP');
        }
        return errors;
    }
    function renderForm() {
        return (React.createElement(React.Fragment, null,
            React.createElement(ModalBody, { dataTestId: "ModalBody-700b0e76" },
                renderErrors(),
                React.createElement("div", { "data-testid": "div-95a86128", className: classNames('mb-4', { error: !!errors.name }) },
                    React.createElement(Label, { dataTestId: "Label-487d4bca", className: "mb-1" },
                        t('REPORT_NAME'),
                        "*"),
                    React.createElement(TextField, { dataTestId: "TextField-1abd63c5", value: formFields === null || formFields === void 0 ? void 0 : formFields.name, onChange: handleChange('name'), error: !!errors.name })),
                React.createElement("div", { "data-testid": "div-39a40491", className: classNames('mb-4', { error: !!errors.description }) },
                    React.createElement(Label, { dataTestId: "Label-86ab1ea4", className: "mb-1" }, t('DESCRIPTION')),
                    React.createElement(TextArea, { dataTestId: "TextArea-36e5deb1", value: formFields === null || formFields === void 0 ? void 0 : formFields.description, onChange: handleChange('description'), error: !!errors.description })),
                React.createElement("div", { "data-testid": "div-e791b93d", className: "grid grid-cols-2 gap-4 mb-4" },
                    React.createElement("div", { "data-testid": "div-ada61555", className: classNames('mt-4', { error: !!errors.group }) },
                        React.createElement(Label, { dataTestId: "Label-ce0028c9", className: "mb-1" },
                            t('SELECT_GROUP'),
                            "*"),
                        React.createElement(CustomDropdown, { dataTestId: "CustomDropdown-7cf1efb5", options: groups || [], onChange: handleSelectGroup, error: !!errors.group, value: formFields === null || formFields === void 0 ? void 0 : formFields.group })),
                    (activeRegions === null || activeRegions === void 0 ? void 0 : activeRegions.length) > 0 && (React.createElement("div", { "data-testid": "div-5f63f6ea", className: "my-4" },
                        React.createElement(Label, { dataTestId: "Label-2564b714", className: "mb-1" }, t('SELECT_REGION')),
                        React.createElement(CustomDropdown, { dataTestId: "CustomDropdown-6e6c6e4c", options: activeRegions.filter((region) => { var _a; return region.parent === ((_a = formFields === null || formFields === void 0 ? void 0 : formFields.group) === null || _a === void 0 ? void 0 : _a._id); }) || [], onChange: handleSelectRegion, error: !!errors.region, value: formFields === null || formFields === void 0 ? void 0 : formFields.region })))),
                React.createElement("div", { "data-testid": "div-91e977eb", className: "my-4 flex items-center" },
                    React.createElement(ToggleSwitchGroup, { dataTestId: "ToggleSwitchGroup-c00ba1b7", onChange: handleChange('shared'), enabled: formFields === null || formFields === void 0 ? void 0 : formFields.shared },
                        t('PUBLIC'),
                        "?"))),
            React.createElement(ModalActions, { dataTestId: "ModalActions-693d3de2", primaryAction: primaryAction, secondaryActions: secondaryActions })));
    }
    return ((React.createElement(Modal, { dataTestId: "Modal-d6ad23aa", title: title, isOpen: isOpen, onClose: onClose },
        isLoading && (React.createElement(ModalBody, { dataTestId: "ModalBody-b5630a66" },
            React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-ddcbc06f" }))),
        isSuccess && renderForm())));
}
